import React, { useEffect, useRef, useState } from "react";
import { FaPause, FaPlay, FaStepBackward, FaStepForward } from "react-icons/fa";

const CustomAudioPlayer = ({ src, waveFormCompleted = null }) => {
  const [isValidSrc, setIsValidSrc] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [waveformData, setWaveformData] = useState([]);
  const audioRef = useRef(null);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const animationRef = useRef(null);
  const resizeTimeoutRef = useRef(null);
  const lastDrawTimeRef = useRef(0);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Validate src when component mounts or src changes
  useEffect(() => {
    if (waveFormCompleted) {
      waveFormCompleted(false);
    }
    setWaveformData([]);
    setCurrentTime(0);
    setDuration(0);
    if (typeof src !== "string") {
      console.error(
        "Invalid src prop provided to CustomAudioPlayer. Expected a string URL but got:",
        src,
      );
      setIsValidSrc(false);
      return;
    }
    setIsValidSrc(true);
  }, [src]);

  useEffect(() => {
    // Only proceed if src is valid
    if (!isValidSrc) return;
    const audio = audioRef.current;
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("error", handleAudioError);
    audio.addEventListener("ended", handleAudioEnded);

    const controller = new AbortController();

    const fetchAudio = async () => {
      try {
        const response = await fetch(src, { signal: controller.signal });
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const reader = response.body.getReader();
        const chunks = [];
        let receivedLength = 0;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          chunks.push(value);
          receivedLength += value.length;
        }

        const blob = new Blob(chunks);
        const arrayBuffer = await blob.arrayBuffer();
        if (isMountedRef.current) {
          processAudio(arrayBuffer);
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Error fetching audio:", error);
        }
      }
    };

    fetchAudio();

    return () => {
      controller.abort();
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("error", handleAudioError);
      audio.removeEventListener("ended", handleAudioEnded);
      cancelAnimationFrame(animationRef.current);
    };
  }, [src, isValidSrc]);

  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (currentTime === 0) return;

    if (
      !lastDrawTimeRef.current ||
      currentTime - lastDrawTimeRef.current > 0.1
    ) {
      drawWaveform();
      lastDrawTimeRef.current = currentTime;
    }
  }, [currentTime]);

  useEffect(() => {
    if (!containerRef.current || waveformData.length === 0 || duration === 0)
      return;

    const resizeObserver = new ResizeObserver((entries) => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }

      resizeTimeoutRef.current = setTimeout(() => {
        updateCanvasSize();
        drawWaveform();
      }, 250);
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, [waveformData, duration]);

  const updateCanvasSize = () => {
    if (!canvasRef.current || !containerRef.current) return;

    const canvas = canvasRef.current;
    const container = containerRef.current;
    const containerWidth = container.clientWidth;

    canvas.width = containerWidth;
  };

  const processAudio = async (arrayBuffer) => {
    if (!containerRef.current) return;
    try {
      const audioContext = new (
        window.AudioContext || window.webkitAudioContext
      )();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

      if (!isMountedRef.current) return;

      const fixedWidth = 1000;

      const waveform = [];
      for (
        let channelIndex = 0;
        channelIndex < audioBuffer.numberOfChannels;
        channelIndex++
      ) {
        const channelData = audioBuffer.getChannelData(channelIndex);
        const samplesPerPixel = Math.floor(channelData.length / fixedWidth);
        const channelWaveform = new Float32Array(fixedWidth);

        for (let i = 0; i < fixedWidth; i++) {
          const startSample = i * samplesPerPixel;
          const endSample = Math.min(
            startSample + samplesPerPixel,
            channelData.length,
          );

          let sum = 0;
          for (let j = startSample; j < endSample; j++) {
            sum += Math.abs(channelData[j]);
          }

          channelWaveform[i] = sum / samplesPerPixel;
        }

        waveform.push(Array.from(channelWaveform));
      }

      if (isMountedRef.current) {
        setWaveformData(waveform);
        setDuration(audioBuffer.duration);

        updateCanvasSize();

        setTimeout(() => {
          if (isMountedRef.current) {
            drawWaveform();
          }
        }, 0);
      }
    } catch (error) {
      if (isMountedRef.current) {
        console.error("Error processing audio:", error);
      }
    }
  };

  const drawWaveform = () => {
    if (
      !canvasRef.current ||
      waveformData.length === 0 ||
      !isMountedRef.current
    )
      return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) return;

    const width = canvas.width;
    const height = canvas.height;

    ctx.clearRect(0, 0, width, height);

    const playedWidth = (currentTime / duration) * width;
    const maxAmplitude = Math.max(
      ...waveformData.flatMap((waveform) =>
        waveform.length > 0 ? waveform : [0],
      ),
    );

    const waveformColors = [
      { played: "#E100E1BF", unplayed: "#8B5CF650" }, // Purple
      { played: "#00A3E0BF", unplayed: "#00A3E050" }, // Blue
    ];

    waveformData.forEach((waveform, waveformIndex) => {
      if (waveform.length === 0) return;

      const colorIndex = waveformIndex % waveformColors.length;
      const colors = waveformColors[colorIndex];
      ctx.lineWidth = 2;

      if (playedWidth > 0) {
        ctx.beginPath();
        let isFirstPointDrawn = false;

        for (let i = 0; i < waveform.length; i++) {
          const x = (i / waveform.length) * width;
          if (x > playedWidth) break;

          const point = waveform[i];
          const y = height - (point / maxAmplitude) * height;

          if (!isFirstPointDrawn) {
            ctx.moveTo(x, y);
            isFirstPointDrawn = true;
          } else {
            ctx.lineTo(x, y);
          }
        }

        ctx.strokeStyle = colors.played;
        ctx.stroke();
      }

      ctx.beginPath();
      let isFirstPointDrawn = false;

      for (let i = 0; i < waveform.length; i++) {
        const x = (i / waveform.length) * width;
        if (x <= playedWidth) continue;

        const point = waveform[i];
        const y = height - (point / maxAmplitude) * height;

        if (!isFirstPointDrawn) {
          ctx.moveTo(x, y);
          isFirstPointDrawn = true;
        } else {
          ctx.lineTo(x, y);
        }
      }

      ctx.strokeStyle = colors.unplayed;
      ctx.stroke();
    });

    if (waveFormCompleted && isMountedRef.current) {
      waveFormCompleted(true);
    }
  };

  const handleLoadedMetadata = () => {
    const audio = audioRef.current;
    if (audio && isFinite(audio.duration)) {
      setDuration(audio.duration);
    } else {
      setDuration(0);
    }
  };

  const handleAudioError = (e) => {
    console.error("Audio error:", e);
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
  };

  const togglePlay = () => {
    const audio = audioRef.current;
    if (isPlaying) {
      audio.pause();
      cancelAnimationFrame(animationRef.current);
    } else {
      audio.play().catch(handleAudioError);
      animationRef.current = requestAnimationFrame(updateTime);
    }
    setIsPlaying(!isPlaying);
  };

  const updateTime = () => {
    const audio = audioRef.current;
    if (audio && isFinite(audio.currentTime)) {
      setCurrentTime(audio.currentTime);
    }
    animationRef.current = requestAnimationFrame(updateTime);
  };

  const seekAudio = (e) => {
    const audio = audioRef.current;
    const canvas = canvasRef.current;
    if (!audio || !canvas) return;

    const rect = canvas.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const canvasWidth = rect.width;

    const clickPosition = clickX / canvasWidth;

    const newTime = clickPosition * audio.duration;
    if (isFinite(newTime)) {
      audio.currentTime = newTime;
      setCurrentTime(newTime);
    }

    if (!isPlaying) {
      audio.play().catch(handleAudioError);
      setIsPlaying(true);
      animationRef.current = requestAnimationFrame(updateTime);
    }
  };

  const skipTime = (seconds) => {
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = Math.max(
        0,
        Math.min(audio.duration, audio.currentTime + seconds),
      );
    }
  };

  const formatTime = (time) => {
    if (!isFinite(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  if (!isValidSrc) {
    return (
      <div className="rounded-xl w-full p-4 bg-red-100 text-red-800">
        Error: Invalid audio source provided. Please provide a valid URL string.
      </div>
    );
  }

  return (
    <div className="rounded-xl w-full" ref={containerRef}>
      <audio ref={audioRef} src={src} />
      <canvas
        ref={canvasRef}
        height="60"
        className="w-full cursor-pointer rounded-lg mb-4 bg-opacity-1"
        onClick={seekAudio}
      />
      <div className="flex items-center justify-between">
        <div className="text-gray-300 font-medium">
          {formatTime(currentTime)} {`/ ${formatTime(duration)}`}
        </div>
        <div className="flex space-x-4">
          <button
            onClick={() => skipTime(-10)}
            className="text-gray-300 hover:text-white transition-colors duration-300"
          >
            <FaStepBackward size={20} />
          </button>
          <button
            onClick={togglePlay}
            className="bg-blue-600 opacity-75 text-white rounded-full p-3 hover:bg-blue-500 transition-all duration-300 w-12 h-12 flex items-center justify-center"
          >
            {isPlaying ? (
              <FaPause size={20} />
            ) : (
              <FaPlay size={20} className="ml-1" />
            )}
          </button>
          <button
            onClick={() => skipTime(10)}
            className="text-gray-300 hover:text-white transition-colors duration-300"
          >
            <FaStepForward size={20} />
          </button>
        </div>
        <div></div>
      </div>
    </div>
  );
};

export default CustomAudioPlayer;
